import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../context/LanguageContext';
import { track, ANALYTICS_EVENTS } from '../lib/analytics';

const SynapsisCTA = () => {
  const { t, language } = useLanguage();
  // Whether this visitor has begun filling the form. The gap between people
  // who start typing and people who send is the most actionable number on the
  // page, so it is fired once rather than on every keystroke.
  const hasStartedRef = useRef(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    website: ''
  });
  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    isError: false,
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ isSubmitting: true, isSubmitted: false, isError: false, message: '' });

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      track(ANALYTICS_EVENTS.CONTACT_SUBMITTED, {
        locale: language,
        has_company: Boolean(formData.company.trim()),
      });

      setFormStatus({
        isSubmitting: false,
        isSubmitted: true,
        isError: false,
        message: t('cta.form.successMessage')
      });
      setFormData({ name: '', email: '', company: '', message: '', website: '' });
    } catch (error) {
      console.error('Email send error:', error);
      // Without this, a broken contact endpoint looks identical to nobody
      // being interested.
      track(ANALYTICS_EVENTS.CONTACT_FAILED, { reason: String(error.message || error) });
      setFormStatus({
        isSubmitting: false,
        isSubmitted: false,
        isError: true,
        message: t('cta.form.errorMessage')
      });
    }
  };

  const handleChange = (e) => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      track(ANALYTICS_EVENTS.CONTACT_STARTED);
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormStatus({ isSubmitting: false, isSubmitted: false, isError: false, message: '' });
  };

  return (
    <section className="py-16 md:py-24 px-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 grid-pattern opacity-10" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center lg:text-left"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              {t('cta.title')} <span className="text-accent-400">{t('cta.titleHighlight')}</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-300 mb-8">
              {t('cta.subtitle')}
            </p>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-accent-500/10 border border-accent-500/20 flex items-center justify-center shrink-0">
                  <BuildingOfficeIcon className="w-6 h-6 text-accent-400" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-white">{t('cta.feature1.title')}</h3>
                  <p className="text-gray-300 text-sm">{t('cta.feature1.subtitle')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-accent-500/10 border border-accent-500/20 flex items-center justify-center shrink-0">
                  <EnvelopeIcon className="w-6 h-6 text-accent-400" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-white">{t('cta.feature2.title')}</h3>
                  <p className="text-gray-300 text-sm">{t('cta.feature2.subtitle')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-accent-500/10 border border-accent-500/20 flex items-center justify-center shrink-0">
                  <PhoneIcon className="w-6 h-6 text-accent-400" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-white">{t('cta.feature3.title')}</h3>
                  <p className="text-gray-300 text-sm">{t('cta.feature3.subtitle')}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="surface p-6 md:p-8 rounded-2xl"
          >
            <AnimatePresence mode="wait">
              {formStatus.isSubmitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex flex-col items-center justify-center py-10 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mb-6">
                    <CheckCircleIcon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{t('cta.form.successTitle')}</h3>
                  <p className="text-gray-300 mb-8 max-w-md">{formStatus.message}</p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={resetForm}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg text-white font-medium transition-all"
                  >
                    {t('cta.form.sendAnother')}
                  </motion.button>
                </motion.div>
              ) : formStatus.isError ? (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="flex flex-col items-center justify-center py-10 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center mb-6">
                    <ExclamationCircleIcon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{t('cta.form.errorTitle')}</h3>
                  <p className="text-gray-300 mb-8 max-w-md">{formStatus.message}</p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={resetForm}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg text-white font-medium transition-all"
                  >
                    {t('cta.form.tryAgain')}
                  </motion.button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="hidden"
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      {t('cta.form.name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all"
                      placeholder={t('cta.form.namePlaceholder')}
                      required
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      {t('cta.form.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all"
                      placeholder={t('cta.form.emailPlaceholder')}
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                      {t('cta.form.company')}
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all"
                      placeholder={t('cta.form.companyPlaceholder')}
                      autoComplete="organization"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      {t('cta.form.message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all resize-none"
                      placeholder={t('cta.form.messagePlaceholder')}
                      required
                    />
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={formStatus.isSubmitting}
                    className="w-full bg-accent-500 hover:bg-accent-600 text-white font-bold py-4 px-8 rounded-lg shadow-glow-accent transition-colors duration-300 flex items-center justify-center space-x-2 disabled:opacity-70"
                  >
                    {formStatus.isSubmitting ? (
                      <>
                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        <span>{t('cta.form.sending')}</span>
                      </>
                    ) : (
                      <>
                        <span>{t('cta.button')}</span>
                        <ArrowRightIcon className="w-5 h-5" />
                      </>
                    )}
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SynapsisCTA;
